import type { PublicPriceTier } from '@repo/api-contracts';
import type { ReactElement, ReactNode } from 'react';
import { activeTierOf } from '@/entities/product';
import { SHIPPING_FEE_EXTRA_NOTE } from '@/entities/quote';
import { formatCount, formatKrw } from '@/shared/lib';
import { Button } from '@/shared/ui';

interface EditorOrderSummaryProps {
  /** 올바른 수량(최소 주문 수량 이상) — 아니면 null. */
  readonly qty: number | null;
  readonly minOrderQty: number;
  readonly tiers: readonly PublicPriceTier[];
  readonly saveStatus: string;
  /** 저장 오류 줄(재시도 포함) — 없으면 null. */
  readonly notice: ReactNode;
  readonly busy: boolean;
  /** [저장] — 비회원은 저장이 없어(G5) null 을 넘기면 버튼을 감추고 [견적 요청하기]만 전폭으로 둔다. */
  readonly onSave: (() => void) | null;
  readonly onRequestQuote: () => void;
}

/**
 * 패널 아래에 붙는 주문 요약 — 상품 상세의 예상 합계 줄과 같은 결(왼쪽 "N개 기준 예상 합계 / 개당", 오른쪽 큰 금액),
 * 저장 상태, 상품 상세 CTA 와 같은 [저장]·[견적 요청하기](회원은 저장 뒤, 비회원은 바로 견적 요청 화면으로).
 */
export const EditorOrderSummary = ({
  qty,
  minOrderQty,
  tiers,
  saveStatus,
  notice,
  busy,
  onSave,
  onRequestQuote,
}: EditorOrderSummaryProps): ReactElement => {
  const tier = activeTierOf(tiers, qty);

  return (
    <div className="space-y-3 border-t border-foreground/10 bg-background pt-4 pb-4 lg:space-y-4 lg:pt-5 lg:pb-0">
      <div aria-live="polite" className="flex items-end justify-between gap-4">
        {qty !== null && tier !== null ? (
          <>
            <div className="space-y-0.5 text-sm">
              <p className="font-semibold tabular-nums">{formatCount(qty)}개 기준 예상 합계</p>
              <p className="text-muted-foreground tabular-nums">
                개당 {formatKrw(tier.unitPrice)} · 부가세 포함
              </p>
              <p className="text-xs text-muted-foreground">{SHIPPING_FEE_EXTRA_NOTE}</p>
            </div>
            <p className="text-2xl leading-none font-bold tracking-[-0.02em] tabular-nums lg:text-[1.75rem]">
              {formatKrw(qty * tier.unitPrice)}
            </p>
          </>
        ) : (
          <p className="text-sm text-muted-foreground">
            {tiers.length === 0
              ? '가격은 견적 상담에서 안내해 드립니다.'
              : `최소 주문 수량은 ${formatCount(minOrderQty)}개입니다.`}
          </p>
        )}
      </div>
      {notice}
      <div
        className={
          onSave === null
            ? 'grid grid-cols-1'
            : 'grid grid-cols-[minmax(0,1fr)_minmax(0,2fr)] gap-2'
        }
      >
        {onSave !== null && (
          <Button
            type="button"
            variant="outline"
            size="lg"
            className="h-12 border-foreground/20 text-base lg:h-14"
            disabled={busy}
            onClick={onSave}
          >
            저장
          </Button>
        )}
        <Button
          type="button"
          size="lg"
          className="h-12 text-base lg:h-14"
          disabled={busy}
          onClick={onRequestQuote}
        >
          견적 요청하기
        </Button>
      </div>
      <p role="status" className="text-xs text-muted-foreground">
        {saveStatus}
      </p>
    </div>
  );
};
